import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { QueryStatus } from '@reduxjs/toolkit/query';

import { useMyStudiesProduct } from 'components/Flux/Personalized/MyStudies/product';
import { ROW_HIGHLIGHT_MS, STUDIES_STATUS_POLL_MS } from 'constants/flux';
import {
  activeStudyIds,
  addHighlights,
  diffStudiesStatus,
  expireHighlights,
  hasStudyStatusChanged,
  nextHighlightExpiry,
  studiesOutsideStatusFilter,
  StudyHighlights,
  StudyReadyEvent,
  toStudyStatusFields,
} from 'helper/flux/diffStudiesStatus';
import { toWireParams } from 'helper/flux/myStudiesAdapter';
import { sendInterfaceErrorToSentry } from 'helper/sendErrorToSentry';
import usePageVisibility from 'utils/hooks/usePageVisibility';
import useStudiesErrorState, {
  StudiesErrorKind,
} from 'utils/hooks/flux/personalized/useStudiesErrorState';
import type { AppDispatch } from 'redux/store';
import { MyStudiesQuery, MyStudyStatusSnapshot } from 'types/Flux/Personalized/myStudies';

export interface UseMyStudiesStatusPollingOptions {
  /**
   * A consulta da página visível: o `listArgs` do `useMyStudiesPage`, o MESMO objeto passado ao
   * `useListMyStudiesQuery`. O hook lê a entrada dela no cache, sem assinar: as linhas, a geração
   * da guarda e o filtro de status (D06). Argumentos equivalentes (status em outra ordem, busca
   * com espaço) caem na mesma entrada e na mesma página.
   */
  listArgs: MyStudiesQuery;
  /** Liga o polling. Desligado, nada é consultado e `error` fica `null`. */
  enabled: boolean;
  /** Intervalo do polling, em ms (padrão `STUDIES_STATUS_POLL_MS`). */
  refreshMs?: number;
}

export interface UseMyStudiesStatusPollingResult {
  /**
   * A página visível tem estudo Em Fila, Executando ou de status desconhecido (bolinha verde ou
   * cinza, D03). Enquanto outra página ou filtro carrega, vale o da última página assentada.
   */
  hasActive: boolean;
  /** O polling está agendado: `enabled`, página assentada com estudo ativo e a aba visível. */
  isPolling: boolean;
  /**
   * Fim da última resposta de status bem-sucedida DA PÁGINA ATUAL (aplicada ou descartada pela
   * guarda), em ms. Preservado nas falhas; `undefined` antes da primeira.
   */
  lastSuccessAt: number | undefined;
  /**
   * Fim da última resposta de status com falha DA PÁGINA ATUAL, em ms; um sucesso depois não a
   * apaga. Muda a cada falha nova: a #2004 a usa para saber se o status falhou de novo depois de
   * um "Tentar de novo" (e então remonta a faixa). `undefined` antes da primeira.
   */
  lastFailureAt: number | undefined;
  /**
   * Idade do que a página mostra, para o indicador e a faixa da #2004: `max(fulfilledTimeStamp da
   * lista, lastSuccessAt)`, os dois da página atual. Nunca regride numa página.
   */
  lastUpdatedAt: number | undefined;
  /**
   * Erro do polling (`useStudiesErrorState`, modo `auxiliary`): fica até uma resposta dar certo,
   * com a aba oculta, na troca dos ids e enquanto outra página carrega. Some só com a página
   * assentada e sem estudo ativo. `null` sem erro.
   */
  error: StudiesErrorKind | null;
  /** Consulta o status na hora ("Tentar de novo"). Sem polling agendado ou já em voo, nada. */
  retry: () => void;
  /**
   * Linhas destacadas, cada uma por `ROW_HIGHLIGHT_MS` desde a mescla (ou o `highlightRows`).
   * Identidade estável entre renders; muda quando um destaque entra, é renovado ou expira.
   */
  changedIds: ReadonlySet<number>;
  /**
   * Avisos de pronto do último retrato aplicado que trouxe algum, já sem os avisados antes (pela
   * `key`). O array só muda de identidade quando chega aviso novo.
   */
  readyEvents: readonly StudyReadyEvent[];
  /** Destaca as linhas por `ROW_HIGHLIGHT_MS` (D12: reenviar e cancelar com sucesso). */
  highlightRows: (ids: readonly number[]) => void;
}

/** Retrato recebido, à espera da lista assentada da página que o pediu. */
interface ReceivedSnapshot {
  seq: number;
  pageKey: string;
  /** Início da requisição de status: a geração do retrato. */
  startedAt: number;
  requestedIds: readonly number[];
  snapshots: readonly MyStudyStatusSnapshot[];
}

/** Instantes do polling de uma página (chave canônica de `listArgs`). */
interface PageStatusTimes {
  /** Fim da última resposta de status bem-sucedida. */
  successAt?: number;
  /** Fim da última resposta, com sucesso ou falha: base do próximo agendamento. */
  settledAt?: number;
  /** Fim da última resposta com falha. */
  failedAt?: number;
}

/** Requisição de status em voo: abortada e solta ao desmontar. */
interface StatusRequest {
  abort: () => void;
  unsubscribe: () => void;
}

/** Retrato mais novo de um estudo, com a geração dele: re-mesclado numa lista mais velha. */
interface KnownSnapshot {
  startedAt: number;
  snapshot: MyStudyStatusSnapshot;
}

const NO_READY_EVENTS: readonly StudyReadyEvent[] = [];
const NO_HIGHLIGHTS: StudyHighlights = new Map();
const SENTRY_TITLE = 'MEUS ESTUDOS: status sem o estudo';

/** Chave canônica da consulta: os parâmetros de wire normalizados, em ordem. */
function pageKeyOf(listArgs: MyStudiesQuery): string {
  const params = toWireParams(listArgs);
  return Object.keys(params).sort().map((key) => `${key}=${params[key]}`).join('&');
}

function latest(...values: (number | undefined)[]): number | undefined {
  const defined = values.filter((value): value is number => value !== undefined);
  return defined.length > 0 ? Math.max(...defined) : undefined;
}

function withPageTimes(
  current: ReadonlyMap<string, PageStatusTimes>,
  pageKey: string,
  update: PageStatusTimes,
): ReadonlyMap<string, PageStatusTimes> {
  const previous = current.get(pageKey) ?? {};
  const next = new Map(current);
  next.set(pageKey, {
    successAt: latest(previous.successAt, update.successAt),
    settledAt: latest(previous.settledAt, update.settledAt),
    failedAt: latest(previous.failedAt, update.failedAt),
  });
  return next;
}

/**
 * Atualização automática do status da página visível da aba Meus Estudos (#2001, nota 4,
 * `.dc.html:49-51`).
 *
 * **Quando consulta.** Só com `enabled`, a aba visível (`usePageVisibility`) e estudo ativo NA
 * PÁGINA VISÍVEL (Em Fila, Executando ou status desconhecido; não em todo o escopo, como o
 * protótipo em `.dc.html:697`). A cadência é pela idade do dado da página: a próxima consulta sai
 * em `max(fulfilledTimeStamp da lista, fim da última consulta desta página) + refreshMs`, via
 * `api.initiateStatus(ids, { forceRefetch: true })`, assinada só
 * até a resposta (o RTK descarta a entrada 60 s depois).
 * - Lista recém-chegada (montagem, outra página, outro filtro, refetch da D06): 1ª consulta
 *   `refreshMs` depois dela, não na hora. Depois, 1 a cada `refreshMs`.
 * - O conjunto de ativos encolher (um estudo terminou) não antecipa a próxima.
 * - Aba oculta: nenhuma requisição. Ao voltar, consulta na hora se o dado tem mais de
 *   `refreshMs`; senão, no fim do intervalo.
 * - Falha: a próxima sai `refreshMs` depois dela ("Tentar de novo" = `retry`, na hora).
 *
 * **Guarda de geração.** A geração da página é o `startedTimeStamp` da requisição da lista que
 * produziu o `currentData`. O retrato só vale com início >= geração; senão, a lista é mais nova e
 * ele é descartado. Com a lista em voo, o retrato espera por ela (sem ser tratado) e vale para a
 * página que o pediu. O retrato aplicado fica guardado por estudo: uma lista mais velha que ele
 * (refetch lento, outra entrada do cache) recebe a mescla de novo, sem destaque nem aviso.
 *
 * **O que faz com o retrato:**
 * - mescla os campos que vieram na entrada da página com a lista do produto (`api.updateList`), sem
 *   refazer a lista; campo ausente no `/status/` mantém o da linha;
 * - `changedIds` (destaque de `ROW_HIGHLIGHT_MS`) e `readyEvents` (transição para pronto
 *   baixável; cada `key` = `${id}:${lastResponse}` sai uma vez só);
 * - id pedido e ausente da resposta: invalida a tag da lista do produto (`api.invalidateList`)
 *   uma vez por id. Se ele continua na página depois do refetch, não invalida mais e avisa o
 *   Sentry uma vez;
 * - regra D06: com filtro de status, uma linha fora dele fica na tela e a página é invalidada
 *   `ROW_HIGHLIGHT_MS` depois da ÚLTIMA saída (`.dc.html:629-633`), sem esmaecer (#1992). Trocar
 *   de página ou de filtro cancela a espera: a lista não tem tag por entrada, e invalidar
 *   refaria a página recém-chegada. A entrada antiga fica com a linha fora do filtro até sair do
 *   cache (`keepUnusedDataFor`, 60 s); se ela voltar à tela antes, a D06 a refaz 2,5 s depois.
 *
 * **Erros.** `useStudiesErrorState(erro, { mode: 'auxiliary' })`, nunca `useHandleRequestErrors`:
 * 500, rede (`FETCH_ERROR` depois das 2 repetições do transporte, ~2,1 s) e 403 de permissão ficam
 * na tela, sem navegar nem zerar o cache; a lista fica intacta e o Sentry recebe 1 evento por
 * sequência de falhas. Só o 403 "JWT Inválido" faz logout, como em toda a aba.
 *
 * **Como a #2004 monta o hook** (na casca `MyStudies/index.tsx`, ao lado do `useMyStudiesPage`):
 * ```tsx
 * const page = useMyStudiesPage(myStudiesFilters, { skip: !isValidUserId(userId), ... });
 * const polling = useMyStudiesStatusPolling({
 *   listArgs: page.listArgs,         // a consulta da lista, nunca outra montada à parte
 *   enabled: isValidUserId(userId),  // o `!skip` da lista; a casca só existe no desktop (D14)
 * });                                // refreshMs: padrão STUDIES_STATUS_POLL_MS
 * ```
 * - Guarda de geração: não passe nada; o hook lê a entrada de `listArgs`.
 * - Destaque: `highlighted={polling.changedIds.has(study.id)}`. Reenviar e cancelar com sucesso
 *   (D12) chamam `polling.highlightRows([id])`: o mesmo mapa e a mesma expiração de 2500 ms.
 * - Aviso: num efeito sobre `polling.readyEvents`, enfileire cada evento pela `key` (ignore uma
 *   `key` já enfileirada: remontar a casca devolve o último lote). Texto "<name> está pronto para
 *   download." (`.dc.html:610`). A mescla já habilita o Baixar.
 * - Indicador e faixa (D03): `polling.lastUpdatedAt` ("atualizado há N s" e "Status não
 *   atualizado desde HH:MM"); bolinha verde com `hasActive`, cinza sem, vermelha com `error`;
 *   "Tentar de novo" → `retry`.
 *
 * @param options - Consulta da página, `enabled` e o intervalo.
 * @returns Estado do polling, destaque e avisos (ver {@link UseMyStudiesStatusPollingResult}).
 */
export default function useMyStudiesStatusPolling({
  listArgs,
  enabled,
  refreshMs = STUDIES_STATUS_POLL_MS,
}: UseMyStudiesStatusPollingOptions): UseMyStudiesStatusPollingResult {
  const { api } = useMyStudiesProduct();
  const dispatch = useDispatch<AppDispatch>();
  const isVisible = usePageVisibility();
  const pageKey = pageKeyOf(listArgs);

  // A página visível, lida do cache da lista (quem assina é o useMyStudiesPage)
  const {
    currentData: page,
    isFetching: listFetching,
    status: listStatus,
    startedTimeStamp: listStartedAt,
    fulfilledTimeStamp: listFulfilledAt,
  } = api.useListQueryState(listArgs, { skip: !enabled });
  // Geração da página: no `fulfilled`, o início da requisição que trouxe o `currentData`. Em voo
  // ou depois de uma falha o RTK já trocou o início: vale o guardado (ou o fim, mais conservador)
  const settledGeneration = listStatus === QueryStatus.fulfilled ? listStartedAt : undefined;
  const generationsRef = useRef(new Map<string, number>());

  const activeIds = useMemo(() => activeStudyIds(page?.items), [page]);
  // Enquanto outra página carrega, vale o "tem ativo" da última assentada (a bolinha não pisca).
  // Sem dado, só a falha assenta: no 1º render da página nova a entrada ainda é `uninitialized`
  const [lastHasActive, setLastHasActive] = useState(false);
  let settledHasActive: boolean | undefined;
  if (page !== undefined) settledHasActive = activeIds.length > 0;
  else if (listStatus === QueryStatus.rejected) settledHasActive = false;
  if (settledHasActive !== undefined && settledHasActive !== lastHasActive) {
    setLastHasActive(settledHasActive);
  }
  const hasActive = enabled && (settledHasActive ?? lastHasActive);

  const [pageTimes, setPageTimes] = useState<ReadonlyMap<string, PageStatusTimes>>(
    () => new Map(),
  );
  const times = pageTimes.get(pageKey);
  const anchorAt = latest(listFulfilledAt, times?.settledAt);

  const [inFlight, setInFlight] = useState<ReadonlyMap<string, number>>(() => new Map());
  const [received, setReceived] = useState<ReceivedSnapshot | null>(null);
  const [pollError, setPollError] = useState<unknown>();
  const [highlights, setHighlights] = useState<StudyHighlights>(NO_HIGHLIGHTS);
  const [readyEvents, setReadyEvents] = useState<readonly StudyReadyEvent[]>(NO_READY_EVENTS);

  const seqRef = useRef(0);
  const handledSeqRef = useRef(0);
  const knownRef = useRef(new Map<number, KnownSnapshot>());
  const announcedRef = useRef(new Set<string>());
  const missingRef = useRef({ invalidated: new Set<number>(), reported: new Set<number>() });
  const requestsRef = useRef(new Set<StatusRequest>());
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    const requests = requestsRef.current;
    return () => {
      aliveRef.current = false;
      requests.forEach((request) => {
        request.abort();
        request.unsubscribe();
      });
      requests.clear();
    };
  }, []);

  useEffect(() => {
    if (settledGeneration !== undefined) generationsRef.current.set(pageKey, settledGeneration);
  }, [pageKey, settledGeneration]);

  const invalidatePage = useCallback(() => {
    dispatch(api.invalidateList());
  }, [api, dispatch]);

  const mergeIntoPage = useCallback((snapshots: readonly MyStudyStatusSnapshot[]) => {
    const byId = new Map(snapshots.map((snapshot) => [snapshot.id, snapshot]));
    dispatch(api.updateList(listArgs, (draft) => {
      draft.items.forEach((study) => {
        const snapshot = byId.get(study.id);
        if (snapshot && hasStudyStatusChanged(study, snapshot)) {
          Object.assign(study, toStudyStatusFields(snapshot));
        }
      });
    }));
  }, [api, dispatch, listArgs]);

  const reportMissing = useCallback((missingIds: readonly number[]) => {
    if (missingIds.length === 0) return;
    const { invalidated, reported } = missingRef.current;
    const fresh = missingIds.filter((id) => !invalidated.has(id));
    if (fresh.length > 0) {
      fresh.forEach((id) => invalidated.add(id));
      invalidatePage();
      return;
    }
    // Já refeita uma vez e o id continua na página: o /status/ não o devolve (escopo diferente)
    const persistent = missingIds.filter((id) => !reported.has(id));
    if (persistent.length === 0) return;
    persistent.forEach((id) => reported.add(id));
    sendInterfaceErrorToSentry(
      SENTRY_TITLE,
      `getMyStudiesStatus: ${persistent.length} id(s) seguem na lista e não vêm no /status/ (ids: ${persistent.join(', ')})`,
      new Error('id ausente do /status/'),
      { tags: { endpoint: 'getMyStudiesStatus' }, fingerprint: ['meus-estudos', 'status', 'id-ausente'] },
    );
  }, [invalidatePage]);

  const startPoll = useCallback(() => {
    if (activeIds.length === 0) return;
    seqRef.current += 1;
    const seq = seqRef.current;
    const requestedKey = pageKey;
    const requestedIds = activeIds;
    const startedAt = Date.now();
    // Assinada até a resposta: sem assinatura, o RTK nunca descartaria a entrada de status
    const request = dispatch(
      api.initiateStatus(requestedIds, { forceRefetch: true }),
    );
    requestsRef.current.add(request);
    setInFlight((current) => new Map(current).set(requestedKey, seq));

    request.then((result) => {
      requestsRef.current.delete(request);
      request.unsubscribe();
      if (!aliveRef.current) return;
      const settledAt = Date.now();
      const ok = !result.isError && result.data !== undefined;
      setInFlight((current) => {
        if (current.get(requestedKey) !== seq) return current;
        const next = new Map(current);
        next.delete(requestedKey);
        return next;
      });
      setPageTimes((current) => withPageTimes(
        current,
        requestedKey,
        ok ? { successAt: settledAt, settledAt } : { settledAt, failedAt: settledAt },
      ));
      if (result.isError) {
        setPollError(result.error);
        return;
      }
      if (result.data === undefined) return;
      setPollError(undefined);
      setReceived({
        seq,
        pageKey: requestedKey,
        startedAt: result.startedTimeStamp ?? startedAt,
        requestedIds,
        snapshots: result.data,
      });
    });
  }, [api, dispatch, activeIds, pageKey]);

  // Agendamento pela idade do dado da página
  const shouldPoll = enabled && isVisible && page !== undefined && activeIds.length > 0;
  const pollingThisPage = inFlight.has(pageKey);
  useEffect(() => {
    if (!shouldPoll || pollingThisPage || anchorAt === undefined) return undefined;
    const timer = setTimeout(startPoll, Math.max(0, anchorAt + refreshMs - Date.now()));
    return () => clearTimeout(timer);
  }, [shouldPoll, pollingThisPage, anchorAt, refreshMs, startPoll]);

  // Lista assentada mais velha que um retrato já aplicado: a mescla volta, sem destaque nem aviso
  useEffect(() => {
    if (page === undefined || listFetching) return;
    const generation = settledGeneration ?? generationsRef.current.get(pageKey) ?? listFulfilledAt;
    if (generation === undefined) return;
    const stale = page.items.flatMap((study) => {
      const known = knownRef.current.get(study.id);
      return known && known.startedAt >= generation && hasStudyStatusChanged(study, known.snapshot)
        ? [known.snapshot] : [];
    });
    if (stale.length > 0) mergeIntoPage(stale);
  }, [pageKey, page, listFetching, settledGeneration, listFulfilledAt, mergeIntoPage]);

  // Cada retrato é tratado uma vez, na página que o pediu e com a lista assentada
  useEffect(() => {
    if (received === null || handledSeqRef.current === received.seq) return;
    if (received.pageKey !== pageKey || page === undefined || listFetching) return;
    handledSeqRef.current = received.seq;

    const generation = settledGeneration ?? generationsRef.current.get(pageKey) ?? listFulfilledAt;
    // A lista saiu depois deste retrato: ela é mais nova
    if (generation !== undefined && received.startedAt < generation) return;

    received.snapshots.forEach((snapshot) => {
      const known = knownRef.current.get(snapshot.id);
      if (!known || known.startedAt <= received.startedAt) {
        knownRef.current.set(snapshot.id, { startedAt: received.startedAt, snapshot });
      }
    });
    const diff = diffStudiesStatus(page.items, received.snapshots, {
      requestedIds: received.requestedIds,
    });
    if (diff.changedIds.length > 0) {
      mergeIntoPage(received.snapshots);
      const until = Date.now() + ROW_HIGHLIGHT_MS;
      setHighlights((current) => addHighlights(current, diff.changedIds, until));
    }
    const fresh = diff.readyEvents.filter((event) => !announcedRef.current.has(event.key));
    if (fresh.length > 0) {
      fresh.forEach((event) => announcedRef.current.add(event.key));
      setReadyEvents(fresh);
    }
    reportMissing(diff.missingIds);
  }, [
    received, pageKey, page, listFetching, settledGeneration, listFulfilledAt, mergeIntoPage,
    reportMissing,
  ]);

  // D06: linha fora do filtro de status da página → refaz 2,5 s depois da última saída
  const outsideFilterKey = page === undefined
    ? '' : studiesOutsideStatusFilter(page.items, listArgs.statuses).join(',');
  useEffect(() => {
    if (outsideFilterKey === '') return undefined;
    const timer = setTimeout(invalidatePage, ROW_HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [pageKey, outsideFilterKey, invalidatePage]);

  // O erro só some com a página assentada e sem nada a atualizar
  const settledWithoutActive = page !== undefined && !listFetching && activeIds.length === 0;
  useEffect(() => {
    if (settledWithoutActive) setPollError(undefined);
  }, [settledWithoutActive]);

  // Expiração do destaque: um timer até o fim mais próximo
  useEffect(() => {
    const expiry = nextHighlightExpiry(highlights);
    if (expiry === null) return undefined;
    const timer = setTimeout(() => {
      setHighlights((current) => expireHighlights(current, Math.max(Date.now(), expiry)));
    }, Math.max(0, expiry - Date.now()));
    return () => clearTimeout(timer);
  }, [highlights]);

  const highlightRows = useCallback((ids: readonly number[]) => {
    const until = Date.now() + ROW_HIGHLIGHT_MS;
    setHighlights((current) => addHighlights(current, ids, until));
  }, []);

  const changedIds = useMemo<ReadonlySet<number>>(() => new Set(highlights.keys()), [highlights]);
  const error = useStudiesErrorState(enabled ? pollError : undefined, { mode: 'auxiliary' });

  const retry = useCallback(() => {
    if (shouldPoll && !pollingThisPage) startPoll();
  }, [shouldPoll, pollingThisPage, startPoll]);

  return {
    hasActive,
    isPolling: shouldPoll,
    lastSuccessAt: times?.successAt,
    lastFailureAt: times?.failedAt,
    lastUpdatedAt: latest(listFulfilledAt, times?.successAt),
    error,
    retry,
    changedIds,
    readyEvents,
    highlightRows,
  };
}
